import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Award, Copy, Printer, Share2 } from "lucide-react";

export default function AfspCertificate() {
  const { user, loading } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/afsp-certificate"); return; }
    api.get("/afsp/certificate")
      .then((r) => setCert(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load certificate"));
  }, [user, loading, nav]);

  const verifyUrl = cert
    ? `${window.location.origin}/verify/${cert.cert_id}`
    : "";

  const copyVerify = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl);
      toast.success("Verify link copied.");
    } catch {
      toast.error("Copy failed — select the link manually.");
    }
  };

  const shareLinkedIn = () => {
    const url = new URL("https://www.linkedin.com/sharing/share-offsite/");
    url.searchParams.set("url", verifyUrl);
    window.open(url.toString(), "_blank", "noopener");
  };

  if (loading) return <div className="p-10 text-muted-foreground">Loading…</div>;

  if (error) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6" data-testid="afsp-cert-error">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-accent/10 text-accent grid place-items-center mb-4">
            <Award size={26} />
          </div>
          <h2 className="font-serif text-3xl mb-2">Not yet earned</h2>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <Button asChild className="w-full" data-testid="afsp-cert-retake">
            <Link to="/afsp-prep">Take the practice exam <Award size={14} className="ml-2" /></Link>
          </Button>
        </Card>
      </div>
    );
  }

  if (!cert) return <div className="p-10 text-muted-foreground">Loading certificate…</div>;

  const issued = new Date(cert.issued_at);
  const issuedStr = issued.toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="min-h-screen bg-secondary/30 py-12 print:bg-white print:py-0" data-testid="afsp-cert-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          @page { size: landscape; margin: 0.4in; }
        }
      `}</style>

      {/* Toolbar */}
      <div className="max-w-5xl mx-auto px-6 mb-6 flex flex-wrap items-center justify-between gap-3 no-print">
        <Button variant="ghost" onClick={() => nav("/afsp-prep")} data-testid="afsp-cert-back">
          <ArrowLeft size={14} className="mr-2" /> Back to AFSP Prep
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copyVerify} data-testid="afsp-cert-copy-verify">
            <Copy size={14} className="mr-2" /> Copy verify link
          </Button>
          <Button variant="outline" onClick={shareLinkedIn} data-testid="afsp-cert-share">
            <Share2 size={14} className="mr-2" /> Share on LinkedIn
          </Button>
          <Button onClick={() => window.print()} data-testid="afsp-cert-print">
            <Printer size={14} className="mr-2" /> Print certificate
          </Button>
        </div>
      </div>

      {/* The certificate itself */}
      <div className="max-w-5xl mx-auto px-6">
        <div
          className="bg-white text-slate-900 rounded-lg shadow-2xl print:shadow-none border-[12px] border-double border-[#2E4FB8] p-10 md:p-16 relative overflow-hidden"
          data-testid="afsp-cert-body"
        >
          {/* Corner ornaments */}
          <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2 border-[#D68A3D]/60" />
          <div className="absolute top-4 right-4 w-16 h-16 border-r-2 border-t-2 border-[#D68A3D]/60" />
          <div className="absolute bottom-4 left-4 w-16 h-16 border-l-2 border-b-2 border-[#D68A3D]/60" />
          <div className="absolute bottom-4 right-4 w-16 h-16 border-r-2 border-b-2 border-[#D68A3D]/60" />

          {/* Header logo + brand */}
          <div className="text-center mb-4">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-16 w-16 mx-auto object-contain mb-3" />
            ) : (
              <div className="h-16 w-16 rounded-md bg-[#2E4FB8] text-white grid place-items-center mx-auto font-serif text-3xl mb-3">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="text-xs uppercase tracking-[0.35em] text-slate-500">
              {branding.brand_name || "DyNamic Tax"}
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <div className="text-xs uppercase tracking-[0.35em] text-[#D68A3D] mb-3">Certificate of Achievement</div>
            <h1 className="font-serif text-3xl md:text-5xl leading-tight">
              AFTR Practice Exam
            </h1>
            <div className="text-sm text-slate-500 mt-2 italic">Annual Filing Season Program · Preparation</div>
          </div>

          {/* Presented to */}
          <div className="text-center mb-8">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">This certifies that</div>
            <div
              className="font-serif text-4xl md:text-6xl text-[#2E4FB8] mb-2 pb-3 border-b border-slate-200 inline-block px-8"
              data-testid="afsp-cert-name"
            >
              {cert.user_name}
            </div>
          </div>

          {/* Body copy */}
          <p className="text-center text-slate-700 leading-relaxed max-w-2xl mx-auto mb-8 text-base md:text-lg">
            has successfully passed the <strong>AFTR-style Practice Examination</strong> covering{" "}
            <em>Circular 230 ethics, filing-season updates, and general federal tax review</em>,
            demonstrating readiness for the IRS Annual Filing Season Program.
          </p>

          {/* Score row */}
          <div className="grid grid-cols-3 gap-6 max-w-xl mx-auto mb-10 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Score</div>
              <div className="font-serif text-3xl mt-1" data-testid="afsp-cert-score">
                {cert.score}<span className="text-slate-400">/{cert.total}</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Percentage</div>
              <div className="font-serif text-3xl mt-1 text-[#2E4FB8]" data-testid="afsp-cert-pct">{cert.percent}%</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Issued</div>
              <div className="font-serif text-3xl mt-1">{issuedStr.split(",")[0]}</div>
              <div className="text-[10px] text-slate-500">{issuedStr.split(",").slice(1).join(",").trim()}</div>
            </div>
          </div>

          {/* Signature row */}
          <div className="flex items-end justify-between max-w-3xl mx-auto gap-8 pt-6 border-t border-slate-200">
            <div className="flex-1">
              <div className="font-serif italic text-2xl text-[#2E4FB8] mb-1">Di'Meka Hamilton</div>
              <div className="h-px bg-slate-300 mb-1.5" />
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Lead Instructor · {branding.brand_name || "DyNamic Tax"}</div>
            </div>
            <div className="flex-1 text-right">
              <div className="font-serif text-lg text-slate-900 mb-1" data-testid="afsp-cert-id">Certificate ID · {cert.cert_id}</div>
              <div className="h-px bg-slate-300 mb-1.5" />
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Verify: {verifyUrl.replace(/^https?:\/\//, "")}
              </div>
            </div>
          </div>

          <div className="text-center text-[10px] text-slate-400 mt-8 uppercase tracking-widest">
            This is a practice-exam certificate. It does not constitute IRS CE credit or the AFSP Record of Completion,
            which is issued only by IRS-approved CE providers.
          </div>
        </div>
      </div>
    </div>
  );
}
